import type { FilterObject } from '@moment/trading-types/filter-expression'
import type {
  AllocationConstraint,
  LadderPolicyConstraint,
  ModelConstraint,
  UniverseConstraint,
} from '@/types/api/constraints'

import {
  AllocationConstraintType,
  MaturityOption,
  UniverseConstraintType,
} from '@moment/horizon-data-access/proposal'
import { addMonths } from 'date-fns/addMonths'
import { format } from 'date-fns/format'
import { parseISO } from 'date-fns/parseISO'

import { generateId } from '@/portfolio/proposal-builder/data/helpers/sleeve-manager'

/** A ladder's maturity basis: its absolute date field plus display label. */
interface MaturityBasis {
  /** Absolute maturity-date field used for rung & universe date filters. */
  field: string
  /** Human-readable basis label, e.g. "maturity date". */
  label: string
}

const MATURITY_BASIS: Record<MaturityOption, MaturityBasis> = {
  [MaturityOption.maturity_date]: {
    field: 'maturity_date',
    label: 'maturity date',
  },
  [MaturityOption.next_call_date]: {
    field: 'next_call_date',
    label: 'next call date',
  },
  [MaturityOption.workout_date]: {
    field: 'workout_date',
    label: 'workout date',
  },
}

// Ignore legacy use_call_adjusted_maturity; default to maturity date.
export function getMaturityBasis(
  constraint: Pick<LadderPolicyConstraint, 'maturity_option'>,
): MaturityBasis {
  return (
    (constraint.maturity_option &&
      MATURITY_BASIS[constraint.maturity_option]) ||
    MATURITY_BASIS[MaturityOption.maturity_date]
  )
}

/**
 * Absolute maturity-date range [referenceDate + startMonths, referenceDate +
 * endMonths) as ISO 'yyyy-MM-dd' strings. parseISO/addMonths/format stay in
 * local time so the integer-month boundaries are timezone-stable.
 */
export function rungDateCondition(
  referenceDate: Date,
  startMonths: number,
  endMonths: number,
): { greater_than_or_equal_to: string; less_than: string } {
  return {
    greater_than_or_equal_to: format(
      addMonths(referenceDate, startMonths),
      'yyyy-MM-dd',
    ),
    less_than: format(addMonths(referenceDate, endMonths), 'yyyy-MM-dd'),
  }
}

/**
 * Resolve the assignment date sent to the optimizer: the sleeve's stored date
 * when it has one, else the next period boundary.
 */
export function resolveLadderAssignmentDate(
  ladder: Pick<LadderPolicyConstraint, 'period_length'> | undefined,
  override?: string,
  now: Date = new Date(),
): string {
  if (override) return override
  if (ladder?.period_length === 1) {
    return format(
      new Date(now.getFullYear(), now.getMonth() + 1, 1),
      'yyyy-MM-dd',
    )
  }
  if (ladder?.period_length === 12) {
    return format(new Date(now.getFullYear() + 1, 0, 1), 'yyyy-MM-dd')
  }
  return format(now, 'yyyy-MM-dd')
}

/**
 * Resolve the date the FE measures a ladder's rung windows from. A rolling
 * ladder (use_assignment_date === false) is re-anchored to the run date by the
 * optimizer, so the stored assignment date does not apply to it.
 */
export function resolveLadderReferenceDate(
  ladder:
    | Pick<LadderPolicyConstraint, 'period_length' | 'use_assignment_date'>
    | undefined,
  assignmentDate?: string,
): string {
  if (ladder?.use_assignment_date === false) {
    return format(new Date(), 'yyyy-MM-dd')
  }
  return resolveLadderAssignmentDate(ladder, assignmentDate)
}

/**
 * The ladder's [start, end) period range. The API allows `num_periods` in
 * place of `end_period` and defaults `start_period` to 0; this resolves both
 * the same way the backend does.
 */
export function getLadderPeriods(
  ladder: Pick<
    LadderPolicyConstraint,
    'start_period' | 'end_period' | 'num_periods'
  >,
): { start: number; end: number } {
  const start = ladder.start_period ?? 0
  const end = ladder.end_period ?? start + (ladder.num_periods ?? 0)
  return { start, end }
}

/**
 * Builds the universe filter that brackets a ladder's full maturity range.
 *
 * Returns the resolved maturity-date field plus a FilterObject covering the
 * absolute date range [assignmentDate + start_period * period_length,
 * assignmentDate + end_period * period_length) months. Shared between the
 * (load-path) expansion and the eligible-universe preview so the two stay in
 * lockstep. assignmentDate must be the already-resolved reference date (callers
 * resolve via resolveLadderAssignmentDate so it is never null).
 */
export function ladderUniverseFilter(
  constraint: LadderPolicyConstraint,
  assignmentDate: string,
): {
  field: string
  filter: FilterObject
} {
  const { period_length } = constraint
  const { start, end } = getLadderPeriods(constraint)
  const { field } = getMaturityBasis(constraint)
  const referenceDate = parseISO(assignmentDate)
  return {
    field,
    filter: {
      [field]: rungDateCondition(
        referenceDate,
        start * period_length,
        end * period_length,
      ),
    },
  }
}

/**
 * Expands a LadderPolicyConstraint into equivalent AllocationConstraint[] + UniverseConstraint.
 *
 * Each ladder period becomes an allocation constraint whose filter is an
 * absolute maturity-date range (measured in whole months from the assignment
 * date), and a single universe constraint covers the full ladder range.
 * assignmentDate must be the already-resolved reference date (callers resolve
 * via resolveLadderAssignmentDate so it is never null).
 */
export function expandLadderPolicyConstraint(
  constraint: LadderPolicyConstraint,
  assignmentDate: string,
): ModelConstraint[] {
  const { period_length, weight_tolerance } = constraint
  const { start, end } = getLadderPeriods(constraint)
  const { field } = getMaturityBasis(constraint)
  const referenceDate = parseISO(assignmentDate)
  const numPeriods = end - start
  const targetWeight = 1 / numPeriods
  // A market_value_tolerance band depends on the portfolio's total value, which
  // the optimizer resolves at run time, so dollar ladders carry only the target.
  const weightBand =
    weight_tolerance == null
      ? {}
      : {
          min_weight: targetWeight - weight_tolerance,
          max_weight: targetWeight + weight_tolerance,
        }

  // Generate one AllocationConstraint per period
  const allocationConstraints: AllocationConstraint[] = []
  for (let p = start; p < end; p++) {
    allocationConstraints.push({
      id: generateId(),
      type: AllocationConstraintType.allocation,
      filter: {
        [field]: rungDateCondition(
          referenceDate,
          p * period_length,
          (p + 1) * period_length,
        ),
      },
      ...weightBand,
      target_weight: targetWeight,
      constraint_priority: constraint.constraint_priority,
    })
  }

  // Single UniverseConstraint covering the full ladder range
  const universeConstraint: UniverseConstraint = {
    id: generateId(),
    type: UniverseConstraintType.universe,
    filter: ladderUniverseFilter(constraint, assignmentDate).filter,
    constraint_priority: constraint.constraint_priority,
  }

  return [...allocationConstraints, universeConstraint]
}
